import _ from 'lodash';
import moment from 'moment';

import {
  getSubscribersFromGuarantorsPrimitive,
  mergeValues,
} from '../../../../components/Guarantor/utils';

import * as store from '../../../../reducers/store';
import * as api from '../../../../api';
import * as utils from '../../../../utils';
import * as logger from '../../../../logger';

export function getEmptyValue(): api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue {
  return {
    subscribers: [],
    addedInsuranceIds: [],
    confirmedInsuranceIds: [],
    invalidInsuranceIds: [],
    unverifiedInsuranceIds: [],
    insuranceCoverages: [],
  };
}

export function parseFieldValue(
  value: string,
  defaultValue: true,
): api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue;
export function parseFieldValue(
  value: string,
  defaultValue?: undefined,
): api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue;
export function parseFieldValue(
  value: string,
  defaultValue: false,
): api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue | undefined;
export function parseFieldValue(
  value: string,
  defaultValue = true,
): api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue | undefined {
  let fieldValue:
    | api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue
    | undefined;
  try {
    fieldValue = JSON.parse(value);
  } catch (err) {
    logger.error(err, 'failed to parse field value for insurance card');
  }
  return fieldValue || (defaultValue ? getEmptyValue() : undefined);
}

function getMergedSubscribers(fieldValue: string, forms: store.Forms) {
  const parsedValue = parseFieldValue(fieldValue);
  const guarantorSubscribers = getSubscribersFromGuarantorsPrimitive(forms);
  const mergedSubscribers = mergeValues(
    parsedValue.subscribers,
    guarantorSubscribers,
  );

  return mergedSubscribers;
}

export function getSubscribersById(fieldValue: string, forms: store.Forms) {
  const mergedSubscribers = getMergedSubscribers(fieldValue, forms);
  return _.mapKeys(mergedSubscribers, 'id');
}

export function getCleanSubscribers(fieldValue: string, forms: store.Forms) {
  const mergedSubscribers = getMergedSubscribers(fieldValue, forms);
  const subscriberOrdering = mergedSubscribers.map((g) => g.id);
  const subscribersById = _.mapKeys(mergedSubscribers, 'id');

  const subscribers = subscriberOrdering
    .map((subId) => {
      const subscriber = subscribersById[subId];
      return {
        ..._.omit(subscriber, 'newlyAdded'),
        // trim string values
        primaryPolicyHolderFirstName:
          subscriber.primaryPolicyHolderFirstName?.trim() ?? null,
        primaryPolicyHolderLastName:
          subscriber.primaryPolicyHolderLastName?.trim() ?? null,
        primaryPolicyHolderEmployer:
          subscriber.primaryPolicyHolderEmployer?.trim() ?? null,
      };
    })
    .filter((subscriber) =>
      // currently selected subscriber should be valid, so clean up invalid entries
      _.every([
        subscriber.primaryPolicyHolderFirstName,
        subscriber.primaryPolicyHolderLastName,
        subscriber.id,
      ]),
    );

  return subscribers;
}

export function getSelfSubscriberId(fieldValue: string, forms: store.Forms) {
  const subscribersById = getSubscribersById(fieldValue, forms);
  const subscriber = _.find(
    subscribersById,
    (s) =>
      s.patientRelationshipToPrimaryPolicyholder ===
      api.Common.Relationship.Self,
  );

  return subscriber!.id;
}

export function getInsuranceCardData(
  card: api.PatientInsuranceInformation.InsuranceInformationWithDetails,
  payorsDisplayNameMapping: Record<string, string>,
) {
  const id = card.id;
  const insuranceProviderImgSrc =
    card.logoUrl && utils.getPrevisitAsset(card.logoUrl);
  const memberName =
    `${[card.primaryPolicyHolderFirstName, card.primaryPolicyHolderLastName]
      .filter(Boolean)
      .join(' ')}` || '-';
  const memberId = card.primaryPolicyHolderMemberId ?? '-';
  const groupNumber = card.groupNumber || '-';
  const coverageStartDate = !!card.coverageStartDate
    ? moment.utc(card.coverageStartDate).format('MM-DD-YYYY')
    : '-';

  const payorDisplayName = getPayorDisplayName({
    payor: card.payor,
    nameRaw: card.nameRaw,
    payorsDisplayNameMapping,
  });

  return {
    id,
    insuranceProviderImgSrc,
    memberName,
    memberId,
    groupNumber,
    coverageStartDate,
    payorDisplayName,
  };
}

export async function asyncCallWithTimeout<T>(
  asyncPromise: Promise<T>,
  timeLimit: number,
  timeoutReason: string = 'Async call timeout limit reached',
): Promise<T | undefined> {
  let timeoutHandle: NodeJS.Timeout;

  const timeoutPromise: Promise<undefined> = new Promise((_, reject) => {
    timeoutHandle = setTimeout(() => reject(timeoutReason), timeLimit);
  });

  return Promise.race([asyncPromise, timeoutPromise]).then((result) => {
    clearTimeout(timeoutHandle);
    return result;
  });
}

export function getPayorDisplayName(args: {
  payor: string | null;
  payorsDisplayNameMapping: Record<string, string>;
  nameRaw?: string | null;
}): string {
  const { payor, nameRaw, payorsDisplayNameMapping } = args;
  if (!payor) return 'Other';
  if (payor.toLowerCase() === 'other' && nameRaw) return nameRaw;
  return payorsDisplayNameMapping[payor] ?? payor;
}
